import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { QRCodeSVG } from 'qrcode.react';
import type { IdentificadorLoja } from '../../api';
import { montarUrlIdentificador } from '../../lib/url-identificador';
import { AvisoPinDispositivo } from './aviso-pin-dispositivo';
import { BotaoPlataforma } from './botao-plataforma';
import { CampoCopiavel } from './campo-copiavel';
import { ROTULO_TIPO_IDENTIFICADOR } from './icone-modo';

/** Sistema do aparelho — o passo a passo muda inteiro entre os dois. */
type Plataforma = 'IOS' | 'ANDROID';

interface Passo {
  titulo: string;
  corpo: ReactNode;
}

/**
 * QR + endereço do ponto de acesso, dentro do primeiro passo. O fundo branco
 * atrás do código é obrigatório, não decoração: no tema escuro o SVG herdaria o
 * fundo da página e nenhuma câmera lê um QR de baixo contraste (mesma razão do
 * `dialog-qrcode.tsx`).
 */
function QrDoAcesso({ url }: { url: string }): JSX.Element {
  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 1, alignSelf: 'flex-start', lineHeight: 0 }}>
        <QRCodeSVG value={url} size={168} level="M" />
      </Box>
      <CampoCopiavel
        rotulo="Endereço do tablet"
        valor={url}
        ajuda="Se a câmera não ler o QR, digite este endereço no navegador do tablet."
      />
    </Stack>
  );
}

/** Caminho de menu do aparelho — em negrito, é o que a pessoa procura na tela. */
function Caminho({ children }: { children: ReactNode }): JSX.Element {
  return <strong>{children}</strong>;
}

function passosIos(url: string): Passo[] {
  return [
    {
      titulo: 'Abrir o cardápio no iPad',
      corpo: (
        <>
          Abra o app <Caminho>Câmera</Caminho> e aponte para o QR code abaixo. Toque no aviso que aparece no alto
          da tela para abrir o cardápio no Safari.
          <QrDoAcesso url={url} />
        </>
      ),
    },
    {
      titulo: 'Instalar como app (tela cheia)',
      corpo: (
        <>
          No Safari, toque no botão <Caminho>Compartilhar</Caminho> (o quadrado com a seta para cima) →{' '}
          <Caminho>Adicionar à Tela de Início</Caminho> → <Caminho>Adicionar</Caminho>. Feche o Safari e abra o
          cardápio pelo ícone novo: assim ele roda sem a barra de endereço.
        </>
      ),
    },
    {
      titulo: 'Definir a mesa deste tablet',
      corpo: (
        <>
          No cardápio, toque em <Caminho>Sobre</Caminho> (canto inferior esquerdo) →{' '}
          <Caminho>Configurações do aparelho</Caminho> e informe o número da mesa. Cada tablet guarda a sua, e sem
          ela o aparelho não deixa pedir.
          <Box sx={{ mt: 1.5 }}>
            <AvisoPinDispositivo />
          </Box>
        </>
      ),
    },
    {
      titulo: 'Preparar a tela',
      corpo: (
        <>
          Em <Caminho>Ajustes → Tela e Brilho → Bloqueio Automático</Caminho>, escolha <Caminho>Nunca</Caminho>.
          Deixe o carregador ligado, gire o iPad para a horizontal e trave a rotação na Central de Controle — o
          tablet fica deitado na mesa.
        </>
      ),
    },
    {
      titulo: 'Ligar o Acesso Guiado',
      corpo: (
        <>
          Em <Caminho>Ajustes → Acessibilidade → Acesso Guiado</Caminho>, ligue a chave. Toque em{' '}
          <Caminho>Ajustes de Código → Definir Código do Acesso Guiado</Caminho> e crie um código de 6 dígitos.
          Anote esse código: é ele que libera o iPad depois.
        </>
      ),
    },
    {
      titulo: 'Travar o iPad na tela do cardápio',
      corpo: (
        <>
          Com o cardápio aberto pelo ícone, aperte <Caminho>3 vezes seguidas o botão superior</Caminho> (iPad com
          Face ID) ou o <Caminho>botão de Início</Caminho> (iPad com botão) e toque em <Caminho>Iniciar</Caminho>,
          no canto superior direito. Pronto: o cliente não sai mais do cardápio.
        </>
      ),
    },
    {
      titulo: 'Para destravar, quando precisar',
      corpo: (
        <>
          Aperte 3 vezes o mesmo botão, digite o código do Acesso Guiado e toque em <Caminho>Encerrar</Caminho>.
        </>
      ),
    },
  ];
}

function passosAndroid(url: string): Passo[] {
  return [
    {
      titulo: 'Abrir o cardápio no tablet',
      corpo: (
        <>
          Abra o app <Caminho>Câmera</Caminho> (ou o leitor de QR do aparelho) e aponte para o QR code abaixo.
          Toque no link que aparece para abrir no Chrome.
          <QrDoAcesso url={url} />
        </>
      ),
    },
    {
      titulo: 'Instalar como app (tela cheia)',
      corpo: (
        <>
          No Chrome, toque no menu de três pontos → <Caminho>Instalar app</Caminho> (ou{' '}
          <Caminho>Adicionar à tela inicial</Caminho>). Abra o cardápio pelo ícone criado: assim ele roda sem a
          barra do navegador.
        </>
      ),
    },
    {
      titulo: 'Definir a mesa deste tablet',
      corpo: (
        <>
          No cardápio, toque em <Caminho>Sobre</Caminho> (canto inferior esquerdo) →{' '}
          <Caminho>Configurações do aparelho</Caminho> e informe o número da mesa. Cada tablet guarda a sua, e sem
          ela o aparelho não deixa pedir.
          <Box sx={{ mt: 1.5 }}>
            <AvisoPinDispositivo />
          </Box>
        </>
      ),
    },
    {
      titulo: 'Preparar a tela',
      corpo: (
        <>
          Em <Caminho>Configurações → Tela → Tempo limite da tela</Caminho>, escolha o maior valor disponível.
          Deixe o carregador ligado, gire o tablet para a horizontal e desligue a rotação automática — o aparelho
          fica deitado na mesa.
        </>
      ),
    },
    {
      titulo: 'Ligar a fixação de tela',
      corpo: (
        <>
          Em <Caminho>Configurações → Segurança</Caminho> (em alguns aparelhos,{' '}
          <Caminho>Segurança e privacidade → Mais configurações</Caminho>), abra <Caminho>Fixação de apps</Caminho>{' '}
          e ligue a chave. Ative também <Caminho>Pedir PIN antes de liberar</Caminho> — sem isso qualquer cliente
          sai do cardápio. Se o tablet ainda não tem bloqueio de tela, crie um PIN em{' '}
          <Caminho>Configurações → Segurança → Bloqueio de tela</Caminho>.
        </>
      ),
    },
    {
      titulo: 'Fixar o cardápio',
      corpo: (
        <>
          Com o cardápio aberto, abra os <Caminho>apps recentes</Caminho> (botão quadrado, ou deslize de baixo para
          cima e segure), toque no ícone do cardápio no topo do cartão e escolha <Caminho>Fixar</Caminho>.
        </>
      ),
    },
    {
      titulo: 'Para soltar, quando precisar',
      corpo: (
        <>
          Segure ao mesmo tempo os botões <Caminho>Voltar</Caminho> e <Caminho>Visão geral</Caminho> (ou deslize de
          baixo para cima e segure) e informe o PIN do tablet.
        </>
      ),
    },
  ];
}

/**
 * Passo a passo de como deixar um tablet travado no cardápio (kiosk), aberto a
 * partir de cada ponto de acesso da aba "Tablet (na mesa)".
 *
 * O trabalho todo acontece NO APARELHO, não aqui: o backoffice só sabia dar o
 * link, e o resto ("como faço o iPad não sair dessa tela?") virava chamado de
 * suporte. Por isso o QR mora dentro do primeiro passo — ele é o começo do
 * roteiro, não um anexo.
 *
 * É uma cartilha, e não um assistente: nada nesta tela fala com o tablet, então
 * não há como conferir etapa nenhuma. Marcar passo como concluído daria uma
 * falsa sensação de progresso.
 */
export function DialogConfigurarTablet({
  identificador,
  aoFechar,
}: {
  /** `null` = fechado. */
  identificador: IdentificadorLoja | null;
  aoFechar: () => void;
}): JSX.Element {
  const theme = useTheme();
  const telaEstreita = useMediaQuery(theme.breakpoints.down('sm'));
  /**
   * A escolha SOBREVIVE ao fechar de propósito: quem monta o salão configura um
   * tablet atrás do outro, todos do mesmo sistema, e refazer a pergunta a cada
   * ponto de acesso seria um clique a mais em cada volta.
   */
  const [plataforma, setPlataforma] = useState<Plataforma | null>(null);
  const [dicaAberta, setDicaAberta] = useState(false);

  const url = identificador ? montarUrlIdentificador(identificador) : '';
  const passos = plataforma === 'IOS' ? passosIos(url) : plataforma === 'ANDROID' ? passosAndroid(url) : [];

  return (
    <Dialog open={!!identificador} onClose={aoFechar} fullWidth maxWidth="sm" fullScreen={telaEstreita}>
      {/* `component="span"` no subtítulo: o DialogTitle é um <h2>, e um <p>
          (padrão do body2) dentro dele é HTML inválido. */}
      <DialogTitle sx={{ pb: 1 }}>
        Configurar tablet
        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
          {identificador?.numero?.trim() || ROTULO_TIPO_IDENTIFICADOR.TABLET}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {!plataforma ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Qual é o tablet que vai ficar na mesa? O caminho para travar o aparelho no cardápio muda em cada
              sistema.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <BotaoPlataforma
                icone={<AppleIcon sx={{ fontSize: 34 }} />}
                titulo="iPad"
                descricao="iPadOS — trava com o Acesso Guiado"
                aoEscolher={() => setPlataforma('IOS')}
              />
              <BotaoPlataforma
                icone={<AndroidIcon sx={{ fontSize: 34 }} />}
                titulo="Android"
                descricao="Samsung, Lenovo, Positivo — trava com a fixação de tela"
                aoEscolher={() => setPlataforma('ANDROID')}
              />
            </Stack>
          </Stack>
        ) : (
          <>
            <Button
              size="small"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={() => setPlataforma(null)}
              sx={{ mb: 1 }}
            >
              {plataforma === 'IOS' ? 'iPad' : 'Android'} — trocar
            </Button>

            {/* Todos os passos abertos: é uma cartilha para seguir com o tablet
                na mão. Abrir um de cada vez esconderia o tamanho do serviço e
                obrigaria a clicar entre uma etapa e outra. */}
            <Stepper orientation="vertical" activeStep={-1} nonLinear>
              {passos.map((passo) => (
                <Step key={passo.titulo} active expanded>
                  <StepLabel>
                    <Typography variant="subtitle2" component="span">
                      {passo.titulo}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" component="div">
                      {passo.corpo}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {plataforma === 'ANDROID' && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setDicaAberta((v) => !v)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transition: 'transform 200ms ease',
                        transform: dicaAberta ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  }
                >
                  Tem muitos tablets?
                </Button>
                <Collapse in={dicaAberta} timeout={300} unmountOnExit>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    A fixação de tela solta quando o aparelho reinicia — alguém precisa fixar de novo. Para um
                    salão inteiro, vale um app de kiosk dedicado (ex.: <strong>Fully Kiosk Browser</strong>), que
                    abre o cardápio sozinho ao ligar e bloqueia a barra de notificações. É pago por aparelho e fica
                    fora do Sischef: instale, aponte para o endereço do passo 1 e ligue o início automático.
                  </Typography>
                </Collapse>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
